import { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'

interface Symbol {
  id: string
  symbol: string
  name: string
  market: string
  currency: string | null
  exchange: string | null
  country: string | null
}

interface SymbolSelectorProps {
  market: 'Commodity' | 'Crypto' | 'Forex' | 'Stock'
  value: string
  onChange: (symbol: string, symbolData?: Symbol) => void
  required?: boolean
  className?: string
}

export default function SymbolSelector({ market, value, onChange, required = false, className }: SymbolSelectorProps) {
  const [symbols, setSymbols] = useState<Symbol[]>([])
  const [filteredSymbols, setFilteredSymbols] = useState<Symbol[]>([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState(value || '')
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const dropdownRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Fetch symbols when market changes
  useEffect(() => {
    const fetchSymbols = async () => {
      setLoading(true)
      try {
        let allSymbols: Symbol[] = []
        let from = 0
        const pageSize = 1000
        let hasMore = true

        // Fetch all symbols in batches (Supabase default limit is 1000 per query)
        while (hasMore) {
          const { data, error } = await supabase
            .from('symbols')
            .select('id, symbol, name, market, currency, exchange, country')
            .eq('market', market)
            .eq('is_active', true)
            .order('symbol', { ascending: true })
            .range(from, from + pageSize - 1)

          if (error) throw error

          if (data && data.length > 0) {
            allSymbols = [...allSymbols, ...data]
            from += pageSize
            // If we got less than pageSize, we've reached the end
            if (data.length < pageSize) {
              hasMore = false
            }
          } else {
            hasMore = false
          }
        }

        setSymbols(allSymbols)
        setFilteredSymbols(allSymbols)
      } catch (err) {
        console.error('Failed to load symbols:', err)
        setSymbols([])
        setFilteredSymbols([])
      } finally {
        setLoading(false)
      }
    }

    fetchSymbols()
  }, [market])

  // Update searchTerm when value changes externally
  useEffect(() => {
    if (value !== searchTerm) {
      setSearchTerm(value)
    }
  }, [value])

  // Filter symbols based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredSymbols(symbols)
      return
    }

    const term = searchTerm.toLowerCase()
    const filtered = symbols.filter(
      (s) =>
        s.symbol.toLowerCase().includes(term) ||
        s.name.toLowerCase().includes(term)
    )
    setFilteredSymbols(filtered)
    setSelectedIndex(-1)
  }, [searchTerm, symbols])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setSearchTerm(newValue)
    setIsOpen(true)
    onChange(newValue)
  }

  const handleSelectSymbol = (symbol: Symbol) => {
    setSearchTerm(symbol.symbol)
    onChange(symbol.symbol, symbol)
    setIsOpen(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        setIsOpen(true)
        return
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setSelectedIndex((prev) => 
          prev < filteredSymbols.length - 1 ? prev + 1 : prev
        )
        break
      case 'ArrowUp':
        e.preventDefault()
        setSelectedIndex((prev) => (prev > 0 ? prev - 1 : 0))
        break
      case 'Enter':
        e.preventDefault()
        if (selectedIndex >= 0 && selectedIndex < filteredSymbols.length) {
          handleSelectSymbol(filteredSymbols[selectedIndex])
        }
        break
      case 'Escape':
        setIsOpen(false)
        break
    }
  }

  const handleInputFocus = () => {
    setIsOpen(true)
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onFocus={handleInputFocus}
        required={required}
        className={className || 'w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent text-sm'}
        placeholder={loading ? 'Loading symbols...' : 'Search symbol...'}
        disabled={loading}
        autoComplete="off"
      />

      {/* Dropdown */}
      {isOpen && filteredSymbols.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filteredSymbols.slice(0, 100).map((symbol, index) => (
            <div
              key={symbol.id}
              onClick={() => handleSelectSymbol(symbol)}
              className={`px-3 py-2 cursor-pointer text-sm hover:bg-orange-50 ${
                index === selectedIndex ? 'bg-orange-100' : ''
              }`}
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">{symbol.symbol}</div>
                  <div className="text-xs text-gray-600 truncate">{symbol.name}</div>
                </div>
                {symbol.exchange && (
                  <div className="text-xs text-gray-500 ml-2">{symbol.exchange}</div>
                )}
              </div>
            </div>
          ))}
          {filteredSymbols.length > 100 && (
            <div className="px-3 py-2 text-xs text-gray-500 text-center border-t">
              Showing first 100 results. Type to narrow down...
            </div>
          )}
        </div>
      )}

      {/* No results message */}
      {isOpen && searchTerm && filteredSymbols.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-3">
          <div className="text-sm text-gray-600 text-center">
            No symbols found for "{searchTerm}"
            <div className="text-xs text-gray-500 mt-1">
              You can still enter custom symbol
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

